import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output } from "@angular/core";
import { firstValueFrom } from "rxjs";
import { createUIError } from "@gtmhub/error-handling";
import { TracingService } from "@webapp/core/tracing/services/tracing.service";
import { localize } from "@webapp/localization/utils/localization.utils";
import { UiModalService } from "@webapp/ui/modal/services/modal.service";
import { UiNotificationService } from "@webapp/ui/notification/services/notification.service";
import { GoalsFacade } from "../../services/goals-facade.service";

@Component({
  selector: "goals-bulk-delete",
  templateUrl: "./goals-bulk-delete.component.html",
  styleUrls: ["./goals-bulk-delete.component.less"],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class GoalsBulkDeleteComponent {
  @Input()
  public goalsIds: string[] = [];
  @Output()
  public readonly deleteCancel = new EventEmitter<void>();
  @Output()
  public readonly deleteSuccess = new EventEmitter<object>();

  constructor(
    private modalService: UiModalService,
    private goalsFacade: GoalsFacade,
    private notificationsService: UiNotificationService,
    private tracingService: TracingService
  ) {}

  public delete(): void {
    const goalsCount = this.goalsIds?.length || 0;
    if (goalsCount <= 0) {
      return;
    }

    this.modalService.confirm({
      uiTitle: goalsCount === 1 ? localize("bulk_delete_num_okrs") : localize("bulk_delete_num_okrs_plural", { count: goalsCount }),
      uiContent: `${localize("bulk_delete_objectives_legend")} ${localize("this_cant_be_undone")}`,
      uiIconType: null,
      uiOkText: localize("delete"),
      uiOkDanger: true,
      uiOnOk: () => this.performBulkDelete(),
      uiCancelText: localize("cancel"),
    });
  }

  public cancel(): void {
    this.deleteCancel.emit();
  }

  private performBulkDelete(): Promise<void> {
    return this.tracingService.traceAction("bulk_delete_goals", () =>
      firstValueFrom(this.goalsFacade.deleteGoals$(this.goalsIds)).then(
        (payload) => {
          if (payload.unsuccessfullyDeletedGoalsCount > 0) {
            throw localize("error_bulk_delete_goals", { ids: payload.unsuccessfullyDeletedGoalsIds.join(", ") });
          }

          this.showSuccessNotification(payload.deletedGoalsCount);
          this.deleteSuccess.emit({ ...payload });
        },
        (error) => {
          throw localize(createUIError(error).title);
        }
      )
    );
  }

  private showSuccessNotification(deletedGoalsCount: number): void {
    const translationKey = deletedGoalsCount === 1 ? "bulk_delete_num_okrs_sucessfully_deleted" : "bulk_delete_num_okrs_sucessfully_deleted_plural";
    const title = localize(translationKey, { count: deletedGoalsCount });
    this.notificationsService.success(title, "", {
      notificationPlacement: "bottomRight",
    });
  }
}
